import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconStar, IconChevron, IconGrip, IconX } from '@/components/ui/Icon';
import { useFavoritesStore } from './useFavoritesStore';
import { favoriteHref, type FavoriteRow } from './resolveFavoriteContext';
import { favoriteIcon } from './entityIcons';

// FLICKER-FREE drag reorder via an INSERTION-LINE indicator.
//
// The rendered row order is ALWAYS the store order (`favorites`) and is NEVER
// mutated during a drag — that invariance is what kills the dragover flicker.
// Instead we render N+1 thin "gap" drop-zones interleaved with the N rows:
//
//     [gap 0]  row 0  [gap 1]  row 1  ...  row N-1  [gap N]
//
// `gap k` means "insert before row k" (and `gap N` means "after the last row").
// Dragging over a gap simply sets `insertIndex = k`; the matching gap paints a
// 2px bar. On drop we move `dragId` to `insertIndex` and call `reorder`.

// Pure. Return `ids` with `dragId` relocated so it lands at slot `insertIndex`,
// where `insertIndex` is measured against the ORIGINAL array (0 = before the
// first row, N = after the last row). Pulling `dragId` out first shifts every
// slot after its old position down by one, so we compensate. No-op-safe.
function moveToIndex(ids: string[], dragId: string, insertIndex: number): string[] {
  const from = ids.indexOf(dragId);
  if (from < 0) return ids;
  const rest = ids.filter((id) => id !== dragId);
  const to = insertIndex > from ? insertIndex - 1 : insertIndex;
  const clamped = to < 0 ? 0 : to > rest.length ? rest.length : to;
  rest.splice(clamped, 0, dragId);
  return rest;
}

function Gap({ k, dragId, insertIndex }: {
  k: number;
  dragId: string | null;
  insertIndex: number | null;
}) {
  return (
    <div className={dragId ? 'h-1.5 flex items-center' : 'h-0'} aria-hidden="true">
      {dragId && insertIndex === k && (
        <div className="h-0.5 w-full bg-primary rounded-full mx-2" />
      )}
    </div>
  );
}

export function FavoritesMenu() {
  const nav = useNavigate();
  const favorites = useFavoritesStore((s) => s.favorites);
  const reorder = useFavoritesStore((s) => s.reorder);
  const remove = useFavoritesStore((s) => s.remove);
  // Nav favorites are starred PAGES (menu items) only; starred records live in
  // the "Follow up" home widget. Reorder sends just these route ids — the
  // backend updates only them, leaving the widget's record order untouched.
  const routes = favorites.filter((f) => f.kind === 'route');

  const [open, setOpen] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  // Floating ghost of the dragged row that follows the cursor.
  const [ghost, setGhost] = useState<{ x: number; y: number; f: FavoriteRow } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Outside-click + Esc close (mirrors DropdownMenu). A drag inside the panel
  // never leaves the panel, so it can't trigger the outside-click close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase + preventDefault: the menu closes first and marks the key
    // used, so a Modal around it stays open and SubNav does not pop the record.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const go = (f: FavoriteRow) => { setOpen(false); triggerRef.current?.focus(); nav(favoriteHref(f)); };

  // POINTER-based drag (no HTML5 dnd — its native drags proved flaky here,
  // starting only for some rows). Pointer capture on the grip: move computes
  // the insertion gap from row midpoints, up commits the reorder. Same
  // pattern as the document line tables.
  const rowEls = useRef(new Map<string, HTMLDivElement>());
  const insertRef = useRef<number | null>(null);
  const startDrag = (e: React.PointerEvent, id: string, index: number) => {
    e.preventDefault();
    setDragId(id);
    setInsertIndex(index);
    insertRef.current = index;
    setGhost({ x: e.clientX, y: e.clientY, f: routes[index] });
    document.body.style.userSelect = 'none';
    const ids = routes.map((f) => f.id);
    const onMove = (ev: PointerEvent) => {
      setGhost((g) => (g ? { ...g, x: ev.clientX, y: ev.clientY } : g));
      let k = ids.length;
      for (let j = 0; j < ids.length; j++) {
        const el = rowEls.current.get(ids[j]);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (ev.clientY < r.top + r.height / 2) { k = j; break; }
      }
      insertRef.current = k;
      setInsertIndex(k);
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      const k = insertRef.current;
      if (k != null) reorder(moveToIndex(ids, id, k));
      setDragId(null); setInsertIndex(null); insertRef.current = null;
      setGhost(null);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  return (
    <div className="relative" ref={ref}>
      <button ref={triggerRef} type="button" title="Favorites" aria-label="Favorites" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 px-2 h-7 rounded-lg text-[0.75rem] text-secondary hover:text-primary-text cursor-pointer select-none">
        <IconStar size={13} />
        <IconChevron size={10} />
      </button>
      {open && (
        <div role="menu"
          className="animate-dropdown absolute right-0 mt-1 w-64 max-h-[70vh] overflow-auto rounded-xl border border-border bg-surface shadow-lg py-1 z-50">
          {routes.length === 0 ? (
            <div className="px-3 py-2 text-[0.75rem] text-muted">No favorite pages yet</div>
          ) : (
            <>
              {routes.map((f, i) => (
                // The rendered order is INVARIANT during a drag: it is always
                // the store order. `i` is the row's fixed number/position.
                <div key={f.id}>
                  <Gap k={i} dragId={dragId} insertIndex={insertIndex} />
                  <div
                    ref={(el) => { if (el) rowEls.current.set(f.id, el); else rowEls.current.delete(f.id); }}
                    className={`group flex items-center gap-2 px-2 py-1 text-[0.75rem] ${dragId === f.id ? 'opacity-40' : ''}`}>
                    <span
                      onPointerDown={(e) => startDrag(e, f.id, i)}
                      title="Drag to reorder"
                      className="cursor-grab active:cursor-grabbing text-muted hover:text-secondary shrink-0 touch-none">
                      <IconGrip size={13} />
                    </span>
                    <span className={`${dragId ? 'pointer-events-none ' : ''}w-4 text-right text-muted font-numeric shrink-0`}>{i + 1}</span>
                    <span className="shrink-0 text-muted [&_svg]:w-[13px] [&_svg]:h-[13px]">{favoriteIcon(f, 13)}</span>
                    <button type="button" onClick={() => go(f)}
                      className={`${dragId ? 'pointer-events-none ' : ''}flex-1 min-w-0 text-left truncate text-secondary hover:text-primary-text`}>{f.label}</button>
                    <button type="button" title="Remove favorite"
                      onClick={(e) => { e.stopPropagation(); remove(f.id); }}
                      className={`${dragId ? 'pointer-events-none ' : ''}shrink-0 text-muted hover:text-danger opacity-0 group-hover:opacity-100 transition`}>
                      <IconX size={12} />
                    </button>
                  </div>
                </div>
              ))}
              {/* Trailing gap N: "insert after the last row". */}
              <Gap k={routes.length} dragId={dragId} insertIndex={insertIndex} />
            </>
          )}
        </div>
      )}
      {/* Dragged-row ghost: a floating copy of the item riding the cursor. */}
      {ghost && (
        <div className="fixed z-system pointer-events-none -translate-y-1/2 rotate-1 rounded-lg border border-border bg-surface shadow-xl px-2.5 py-1 text-[0.75rem] flex items-center gap-2"
          style={{ left: ghost.x + 14, top: ghost.y }}>
          <span className="shrink-0 text-muted [&_svg]:w-[13px] [&_svg]:h-[13px]">{favoriteIcon(ghost.f, 13)}</span>
          <span className="max-w-[220px] truncate text-secondary">{ghost.f.label}</span>
        </div>
      )}
    </div>
  );
}
